import {useEffect, useRef} from 'react';
import {useDashboardMeContext} from '@/dashboard/me-context';
import {authBearer} from '@/lib/portal-api';

/** Registration-specific orchestration over the existing portal bearer fence.
 * A sent request stays recoverable; leaving its screen forbids another step. */
export function useRegistrationOperation(context: string) {
  const me = useDashboardMeContext()?.me;
  const scope = JSON.stringify([context, me?.organizationId, me?.role, me?.clinicIds]);
  const latest = useRef({scope});
  if (latest.current.scope !== scope) latest.current = {scope};
  const captured = latest.current;
  const mounted = useRef(false);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const isCurrent = () => mounted.current && latest.current === captured;
  const check = async (bearer: string) => {
    const current = await authBearer();
    if (!isCurrent() || current !== bearer) throw new Error('Your registration session changed. Reopen the saved request in its original session.');
  };
  const begin = async () => {
    const bearer = await authBearer();
    if (!bearer || !isCurrent()) throw new Error('The registration session is no longer available.');
    return bearer;
  };
  return {begin, check, isCurrent};
}
